import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { PreviewProvider } from './contexts/PreviewContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './components/Login';
import Register from './components/Register';
import VerifyEmail from './components/VerifyEmail';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import AdminPanel from './components/AdminPanel';
import PortfolioBlenderApp from './apps/PortfolioBlenderApp';
import ResearchReportsApp from './apps/ResearchReportsApp';
import FundScreenerApp from './apps/FundScreenerApp';
import PortfolioViewerApp from './apps/PortfolioViewerApp';
import ResourcesApp from './apps/ResourcesApp';
import PortfolioConstructorApp from './apps/PortfolioConstructorApp';
import FactSheetApp from './apps/FactSheetApp';
import PortfolioReportsApp from './apps/PortfolioReportsApp';
import InvestmentPerspectivesApp from './apps/InvestmentPerspectivesApp';
import PortfolioAttributionApp from './apps/PortfolioAttributionApp';
import PortfolioComparisonApp from './apps/PortfolioComparisonApp';
import PortfolioDashboardApp from './apps/PortfolioDashboardApp';
import RetirementCalculatorApp from './apps/RetirementCalculatorApp/RetirementCalculatorApp';
import AllocationCalculatorApp from './apps/AllocationCalculatorApp/AllocationCalculatorApp';
import RiskModelApp from './apps/RiskModelApp';
import BacktestDashboardApp from './apps/BacktestDashboardApp';
import MarketDataApp from './apps/MarketDataApp';
import CpdApp from './apps/CpdApp';
import SoaBuilderApp from './apps/SoaBuilderApp/SoaBuilderApp';
import PortfolioLabApp from './apps/PortfolioLabApp';
import PortfolioOptimiserApp from './apps/PortfolioOptimiserApp';
import InvestmentConfigApp from './apps/InvestmentConfigApp';
import ProjectsApp from './apps/ProjectsApp';
import TradingApp from './apps/TradingApp';
import DbExplorerApp from './apps/DbExplorerApp/DbExplorerApp';
import TacticalMonitorApp from './apps/TacticalMonitorApp/TacticalMonitorApp';
import GrowthBarometerApp from './apps/GrowthBarometerApp/GrowthBarometerApp';
import DriftMonitoringApp from './apps/DriftMonitoringApp/DriftMonitoringApp';
import TailoredTAAMonitoringApp from './apps/TailoredTAAMonitoringApp/TailoredTAAMonitoringApp';
import PlusFundApp from './apps/PlusFundApp/PlusFundApp';
import SectorHeatmapApp from './apps/SectorHeatmapApp/SectorHeatmapApp';
import ManagerSummaryApp from './apps/ManagerSummaryApp/ManagerSummaryApp';
import QuantScreenerApp from './apps/QuantScreenerApp/QuantScreenerApp';
import AnalystPortfoliosApp from './apps/AnalystPortfoliosApp/AnalystPortfoliosApp';
import FAQPage from './components/FAQPage';

// Import Bootstrap
import 'bootstrap/dist/css/bootstrap.min.css';
import './index.css';

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="spinner-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

// Public Route (redirect to dashboard if logged in)
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="spinner-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return !isAuthenticated ? children : <Navigate to="/dashboard" replace />;
}

// App permission guard - redirects to dashboard if user lacks permission
function AppRoute({ appId, children }) {
  const { user } = useAuth();
  if (user && !user.is_drummond_user && !user.allowed_apps?.includes(appId)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

// Layout for protected routes with Navbar and Footer
function ProtectedLayout({ children }) {
  return (
    <div className="d-flex flex-column min-vh-100">
      <Navbar />
      <main className="flex-grow-1">
        <ErrorBoundary>{children}</ErrorBoundary>
      </main>
      <Footer />
    </div>
  );
}

// App Content with routing
function AppContent() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />
        <Route
          path="/verify-email/:token"
          element={<VerifyEmail />}
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password/:token"
          element={<ResetPassword />}
        />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <Dashboard />
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/portfolio-blender"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="portfolio_blender"><PortfolioBlenderApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/research-reports"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="research_reports"><ResearchReportsApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/fund-screener"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="fund_screener"><FundScreenerApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/portfolio-viewer"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="portfolio_viewer"><PortfolioViewerApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/resources"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="resources"><ResourcesApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/portfolio-constructor"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="portfolio_constructor"><PortfolioConstructorApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/factsheets"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="fact_sheets"><FactSheetApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/portfolio-reports"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="portfolio_reports"><PortfolioReportsApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/investment-perspectives"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="investment_perspectives"><InvestmentPerspectivesApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/portfolio-attribution"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="portfolio_attribution"><PortfolioAttributionApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/portfolio-comparison"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="portfolio_comparison"><PortfolioComparisonApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/portfolio-lab"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="portfolio_lab"><PortfolioLabApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/portfolio-dashboard"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="portfolio_dashboard"><PortfolioDashboardApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/retirement-calculator"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="retirement_calculator"><RetirementCalculatorApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/allocation-calculator"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="allocation_calculator"><AllocationCalculatorApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/risk-model"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="risk_model"><RiskModelApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/backtest-dashboard"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="backtest_dashboard"><BacktestDashboardApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/market-data"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="market_data"><MarketDataApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/cpd"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="cpd"><CpdApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/portfolio-optimiser"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="portfolio_optimiser"><PortfolioOptimiserApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/investment-config"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="investment_config"><InvestmentConfigApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/projects"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="projects"><ProjectsApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/trading"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="portfolio_trading"><TradingApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/soa-builder"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="soa_builder"><SoaBuilderApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/db-explorer"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="db_explorer"><DbExplorerApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/tactical-monitor"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="tactical_monitor"><TacticalMonitorApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/growth-barometer"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="growth_barometer"><GrowthBarometerApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/drift-monitoring"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="drift_monitoring"><DriftMonitoringApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/tailored-taa-monitoring"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="tailored_taa_monitoring"><TailoredTAAMonitoringApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apps/plus-fund"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="plus_fund"><PlusFundApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/apps/sector-heatmap"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="sector_heatmap"><SectorHeatmapApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/apps/manager-summary"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="manager_summary"><ManagerSummaryApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/apps/quant-screener"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="quant_screener"><QuantScreenerApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/apps/analyst-portfolios"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AppRoute appId="analyst_portfolios"><AnalystPortfoliosApp /></AppRoute>
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <AdminPanel />
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/faq"
          element={
            <ProtectedRoute>
              <ProtectedLayout>
                <FAQPage />
              </ProtectedLayout>
            </ProtectedRoute>
          }
        />

        {/* Default route */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

// Main App with providers
function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <PreviewProvider>
          <AppContent />
        </PreviewProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
